<template>
  <component
    :is="computedTag"
    v-bind="componentAttrs"
    :class="classes"
    @click="onClick"
  >
    <!-- Normal content when not loading -->
    <template v-if="!loading">
      <slot />
    </template>

    <!-- Loading content: custom slot or spinner + text -->
    <template v-else>
      <slot name="loading">
        <span v-if="loadingFill">
          <span class="spinner-border spinner-border-sm"></span>
        </span>
        <span v-else>
          <span class="spinner-border spinner-border-sm me-2"></span>
          {{ loadingText }}
        </span>
      </slot>
    </template>
  </component>
</template>

<script>
export default {
  name: "BaseButton",
  inheritAttrs: false,

  props: {
    /** Router link target */
    to: { type: [String, Object], default: null },
    replace: { type: Boolean, default: false },
    activeClass: { type: String, default: "router-link-active" },
    exactActiveClass: { type: String, default: "router-link-exact-active" },
    routerComponentName: { type: String, default: "router-link" },
    routerTag: { type: String, default: "a" },

    /** Plain anchor href */
    href: { type: String, default: null },

    /** Fallback tag */
    tag: { type: String, default: "button" },
    type: { type: String, default: "button" }, // button|submit|reset

    /** Visual variants */
    variant: { type: String, default: "secondary" },
    outline: { type: Boolean, default: false },
    size: { type: String, default: null }, // sm|lg
    block: { type: Boolean, default: false },
    pill: { type: Boolean, default: false },
    squared: { type: Boolean, default: false },

    /** State */
    active: { type: Boolean, default: false },
    pressed: { type: Boolean, default: null }, // tri-state
    disabled: { type: Boolean, default: false },

    /** Loading */
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: "Loading..." },
    loadingFill: { type: Boolean, default: false },
  },

  computed: {
    // Choose element: RouterLink / <a> / <button> / custom tag
    computedTag() {
      if (this.to != null) return this.routerComponentName;
      else if (this.href != null) return "a";
      else return this.tag;
    },

    // Build Bootstrap classes
    classes() {
      const cls = ["btn"];

      // Variant
      if (this.outline) cls.push(`btn-outline-${this.variant}`);
      else cls.push(`btn-${this.variant}`);

      // Size
      if (this.size === "sm" || this.size === "lg") {
        cls.push(`btn-${this.size}`);
      }

      // Block-level
      if (this.block) cls.push("btn-block");

      // Shapes
      if (this.pill) cls.push("rounded-pill");
      else if (this.squared) cls.push("rounded-0");

      // Active state
      if (this.active || this.pressed === true) cls.push("active");

      // Disabled/loading state
      if (this.disabled || this.loading) cls.push("disabled");

      return cls;
    },

    // Map props + attrs to the rendered element
    componentAttrs() {
      const a = { ...this.$attrs };

      // RouterLink props
      if (this.to != null) {
        a.to = this.to;
        a.replace = this.replace;
        a.activeClass = this.activeClass;
        a.exactActiveClass = this.exactActiveClass;
        a.tag = this.routerTag;
      }
      // Anchor props
      else if (this.href != null) {
        a.href = this.href;
        if (this.disabled || this.loading) {
          a["aria-disabled"] = "true";
          a.tabindex = "-1";
        }
      }
      // Button props
      else {
        a.type = this.type;
        if (this.disabled || this.loading) {
          a.disabled = true;
        }
      }

      // ARIA-pressed
      if (this.pressed === true) a["aria-pressed"] = "true";
      if (this.pressed === false) a["aria-pressed"] = "false";

      return a;
    },
  },

  methods: {
    onClick(evt) {
      if (this.disabled || this.loading) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        return;
      }
      this.$emit("click", evt);
    },
  },
};
</script>
